import { supabase } from '../lib/supabase';

// Parse user agent for device/browser info
const parseUserAgent = (ua = navigator?.userAgent || '') => {
  const browser =
    /Chrome\/[\d.]+/?.test(ua) && !/Edg\//?.test(ua) ? 'Chrome' :
    /Firefox\/[\d.]+/?.test(ua) ? 'Firefox' :
    /Safari\/[\d.]+/?.test(ua) && !/Chrome/?.test(ua) ? 'Safari' :
    /Edg\/[\d.]+/?.test(ua) ? 'Edge' :
    /OPR\/[\d.]+/?.test(ua) ? 'Opera' : 'Unknown Browser';

  const os =
    /Windows NT/?.test(ua) ? 'Windows' :
    /Mac OS X/?.test(ua) ? 'macOS': /Linux/?.test(ua) ?'Linux': /Android/?.test(ua) ?'Android': /iPhone|iPad/?.test(ua) ?'iOS' : 'Unknown OS';

  const deviceType =
    /Mobile|Android|iPhone/?.test(ua) ? 'mobile' :
    /iPad|Tablet/?.test(ua) ? 'tablet' : 'desktop';

  return { browser, os, deviceType };
};

const getDeviceInfo = () => {
  const ua = navigator?.userAgent || '';
  const { browser, os, deviceType } = parseUserAgent(ua);
  return {
    browser,
    os,
    deviceType,
    userAgent: ua,
    screenResolution: `${window?.screen?.width}x${window?.screen?.height}`,
    language: navigator?.language || 'unknown',
    platform: navigator?.platform || 'unknown',
  };
};

// Log a security event for the current user
export const logSecurityEvent = async (userId, eventType, status = 'success', metadata = {}) => {
  if (!userId) return null;
  try {
    const deviceInfo = getDeviceInfo();
    const { data, error } = await supabase?.rpc('log_security_event', {
      p_user_id: userId,
      p_event_type: eventType,
      p_event_status: status,
      p_ip_address: null, // IP not available client-side; server-side would populate
      p_user_agent: deviceInfo?.userAgent,
      p_device_info: {
        browser: deviceInfo?.browser,
        os: deviceInfo?.os,
        deviceType: deviceInfo?.deviceType,
        screenResolution: deviceInfo?.screenResolution,
        language: deviceInfo?.language,
      },
      p_metadata: metadata,
    });
    if (error) {
      console.warn('Failed to log security event:', error?.message);
    }
    return data;
  } catch (err) {
    console.warn('Security log error:', err?.message);
    return null;
  }
};

// Fetch security audit logs for a user
export const getSecurityLogs = async (userId, limit = 50) => {
  if (!userId) return { data: [], error: null };
  try {
    const { data, error } = await supabase
      ?.from('security_audit_logs')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);
    return { data: data || [], error };
  } catch (err) {
    return { data: [], error: err };
  }
};

// Upsert current session record
export const upsertCurrentSession = async (userId) => {
  if (!userId) return null;
  try {
    const deviceInfo = getDeviceInfo();
    const sessionId = `session_${userId}_${Date.now()}`;

    // Mark all previous sessions as not current
    await supabase
      ?.from('user_sessions')
      ?.update({ is_current: false })
      ?.eq('user_id', userId)
      ?.eq('is_current', true);

    const { data, error } = await supabase
      ?.from('user_sessions')
      ?.insert({
        user_id: userId,
        session_token: sessionId,
        device_name: `${deviceInfo?.browser} on ${deviceInfo?.os}`,
        device_type: deviceInfo?.deviceType,
        browser: deviceInfo?.browser,
        os: deviceInfo?.os,
        ip_address: null,
        is_current: true,
        last_active_at: new Date()?.toISOString(),
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString(),
      })
      ?.select()
      ?.single();

    return { data, error };
  } catch (err) {
    return { data: null, error: err };
  }
};

// Get all sessions for a user
export const getUserSessions = async (userId) => {
  if (!userId) return { data: [], error: null };
  try {
    const { data, error } = await supabase
      ?.from('user_sessions')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('last_active_at', { ascending: false })
      ?.limit(20);
    return { data: data || [], error };
  } catch (err) {
    return { data: [], error: err };
  }
};

// Revoke a session
export const revokeSession = async (sessionId) => {
  try {
    const { error } = await supabase
      ?.from('user_sessions')
      ?.delete()
      ?.eq('id', sessionId);
    return { error };
  } catch (err) {
    return { error: err };
  }
};

// Revoke all other sessions (keep current)
export const revokeAllOtherSessions = async (userId) => {
  try {
    const { error } = await supabase
      ?.from('user_sessions')
      ?.delete()
      ?.eq('user_id', userId)
      ?.eq('is_current', false);
    return { error };
  } catch (err) {
    return { error: err };
  }
};

export const securityService = {
  logSecurityEvent,
  getSecurityLogs,
  upsertCurrentSession,
  getUserSessions,
  revokeSession,
  revokeAllOtherSessions,
};

export default securityService;
